import {
  type DocumentMeta,
  type DocumentVersion
} from "@prismicio/editor-fields"
import { useRequest } from "@prismicio/editor-support/Suspense"
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Text
} from "@prismicio/editor-ui"
import { useCallback, useState } from "react"

import {
  getPreviewUrls,
  getRedirectURL,
  type PreviewUrl,
  refreshRelease
} from "../../app/services/core"
import { trackPreviewOpen } from "../../app/tracking"
import { useDocument } from "../DocumentProvider"
import { useIsVersionInMigrationRelease } from "../documentRouting"

export function PreviewMenu() {
  const { data } = useDocument()

  const { results: previewOptions } = usePreviewUrls()

  const [contentWidth, setContentWidth] = useState<number>()
  const [loading, setLoading] = useState(false)

  const getRedirectUrl = useGetRedirectUrl()

  if (data.type === "new") return null
  const { meta, version } = data

  const buttonRefCallback = (button: HTMLButtonElement | null) => {
    if (!button) return
    setContentWidth(button.clientWidth)
  }

  const openPreview = (previewOption: PreviewUrl) => {
    trackPreviewOpen({
      label: previewOption.label,
      documentId: meta.id
    })

    setLoading(true)

    getRedirectUrl(previewOption, meta, version)
      .then((body) => window.open(body.preview_url))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  return (
    <Box flexDirection="column">
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            ref={buttonRefCallback}
            color="grey"
            startIcon="visibility"
            size="large"
            loading={loading}
          >
            Preview the page
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>
            {previewOptions.length > 0
              ? "Select preview"
              : "Preview not enabled"}
          </DropdownMenuLabel>
          <Box width={contentWidth} flexDirection="column">
            {previewOptions.map((option) => (
              <DropdownMenuItem
                key={option.id}
                onSelect={() => openPreview(option)}
              >
                <Text noWrap>{option.label}</Text>
              </DropdownMenuItem>
            ))}
          </Box>
        </DropdownMenuContent>
      </DropdownMenu>
    </Box>
  )
}

function usePreviewUrls() {
  return useRequest(getPreviewUrls, [])
}

function useGetRedirectUrl() {
  const isVersionInMigrationRelease = useIsVersionInMigrationRelease()

  return useCallback(
    async (
      previewOption: PreviewUrl,
      meta: DocumentMeta,
      version: DocumentVersion
    ) => {
      if (isVersionInMigrationRelease(version)) {
        await refreshRelease(version.release_id)
      }

      return getRedirectURL(previewOption.id, meta.id, version)
    },
    [isVersionInMigrationRelease]
  )
}
